import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { Truck, PackageCheck, AlertCircle, CheckCircle, XCircle } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ASCDispatches = () => {
  const [dispatches, setDispatches] = useState([]);
  const [filterStatus, setFilterStatus] = useState('all');
  const [showReturnModal, setShowReturnModal] = useState(false);
  const [selectedDispatch, setSelectedDispatch] = useState(null);
  const [returnData, setReturnData] = useState({
    returned_device_ids: [],
    repair_status: 'repaired',
    remarks: ''
  });
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    loadDispatches();
  }, [filterStatus]);

  const loadDispatches = async () => {
    try {
      const token = localStorage.getItem('token');
      let url = `${API_URL}/api/asc/dispatches`;
      if (filterStatus !== 'all') {
        url += `?status=${filterStatus}`;
      }

      const response = await axios.get(url, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.success) {
        setDispatches(response.data.dispatches);
      }
    } catch (error) {
      console.error('Error loading dispatches:', error);
    }
  };

  const openReturnModal = (dispatch) => {
    setSelectedDispatch(dispatch);
    setReturnData({
      returned_device_ids: dispatch.device_ids,
      repair_status: 'repaired',
      remarks: ''
    });
    setShowReturnModal(true);
  };

  const handleReturn = async () => {
    if (returnData.returned_device_ids.length === 0) {
      alert('Please select at least one device');
      return;
    }

    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API_URL}/api/asc/return`,
        {
          dispatch_id: selectedDispatch.dispatch_id,
          returned_device_ids: returnData.returned_device_ids,
          parts_returned: [],
          repair_status: returnData.repair_status,
          remarks: returnData.remarks
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.success) {
        alert(`Received ${returnData.returned_device_ids.length} devices from ASC!`);
        setShowReturnModal(false);
        setSelectedDispatch(null);
        loadDispatches();
      }
    } catch (error) {
      console.error('Error receiving devices:', error);
      alert(error.response?.data?.detail || 'Error receiving devices');
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      dispatched: 'bg-blue-100 text-blue-800',
      in_repair: 'bg-yellow-100 text-yellow-800',
      returned: 'bg-green-100 text-green-800'
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  const getStatusIcon = (status) => {
    const icons = {
      dispatched: <Truck className="w-5 h-5" />,
      in_repair: <AlertCircle className="w-5 h-5" />,
      returned: <PackageCheck className="w-5 h-5" />
    };
    return icons[status] || <Truck className="w-5 h-5" />;
  };

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <Truck className="w-8 h-8 text-blue-600" />
            <h1 className="text-3xl font-bold text-gray-900">ASC Dispatches</h1>
          </div>
          <button
            onClick={() => navigate('/asc/manage')}
            className="px-4 py-2 border-2 border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
          >
            Back to ASC List
          </button>
        </div>

        {/* Filter */}
        <div className="bg-white rounded-lg shadow p-4 mb-6">
          <div className="flex gap-3">
            {['all', 'dispatched', 'in_repair', 'returned'].map(status => (
              <button
                key={status}
                onClick={() => setFilterStatus(status)}
                className={`px-4 py-2 rounded-lg font-semibold transition-all ${
                  filterStatus === status
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                {status === 'all' ? 'All' : status.replace('_', ' ').toUpperCase()}
              </button>
            ))}
          </div>
        </div>

        {/* Dispatches List */}
        <div className="space-y-4">
          {dispatches.map((dispatch, index) => {
            const isOverdue = dispatch.status === 'dispatched' && 
              new Date(dispatch.expected_return_date) < new Date();

            return (
              <div key={index} className="bg-white rounded-lg shadow hover:shadow-lg transition-shadow">
                <div className="p-6">
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <h3 className="text-xl font-bold text-gray-900">
                          {dispatch.dispatch_id}
                        </h3>
                        <span className={`px-3 py-1 rounded-full text-xs font-semibold flex items-center gap-1 ${getStatusColor(dispatch.status)}`}>
                          {getStatusIcon(dispatch.status)}
                          {dispatch.status.toUpperCase()}
                        </span>
                        {isOverdue && (
                          <span className="px-3 py-1 rounded-full text-xs font-semibold bg-red-100 text-red-800">
                            OVERDUE
                          </span>
                        )}
                      </div>
                      <p className="text-gray-600">{dispatch.asc_name}</p>
                    </div>
                    {dispatch.status === 'dispatched' && (
                      <button
                        onClick={() => openReturnModal(dispatch)}
                        className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 font-semibold"
                      >
                        Mark as Returned
                      </button>
                    )}
                  </div>

                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                    <div>
                      <span className="text-sm text-gray-600">Total Devices</span>
                      <p className="text-lg font-bold text-gray-900">{dispatch.total_devices}</p>
                    </div>
                    <div>
                      <span className="text-sm text-gray-600">Dispatched On</span>
                      <p className="text-sm font-semibold text-gray-900">
                        {new Date(dispatch.dispatched_at).toLocaleDateString()}
                      </p>
                    </div>
                    <div>
                      <span className="text-sm text-gray-600">Expected Return</span>
                      <p className={`text-sm font-semibold ${isOverdue ? 'text-red-600' : 'text-gray-900'}`}>
                        {new Date(dispatch.expected_return_date).toLocaleDateString()}
                      </p>
                    </div>
                    {dispatch.actual_return_date && (
                      <div>
                        <span className="text-sm text-gray-600">Actual Return</span>
                        <p className="text-sm font-semibold text-gray-900">
                          {new Date(dispatch.actual_return_date).toLocaleDateString()}
                        </p>
                      </div>
                    )}
                  </div>

                  {dispatch.remarks && (
                    <div className="p-3 bg-blue-50 rounded-lg border border-blue-200">
                      <p className="text-sm text-blue-900">
                        <strong>Remarks:</strong> {dispatch.remarks}
                      </p>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        {dispatches.length === 0 && (
          <div className="bg-white rounded-lg shadow p-12 text-center">
            <Truck className="w-16 h-16 mx-auto mb-4 text-gray-300" />
            <p className="text-gray-600 text-lg">No dispatches found</p>
          </div>
        )}

        {/* Return Modal */}
        {showReturnModal && selectedDispatch && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg shadow-xl p-6 max-w-2xl w-full m-4">
              <h2 className="text-2xl font-bold text-gray-900 mb-6">Receive Devices from ASC</h2>

              <div className="mb-4">
                <p className="text-gray-600">Dispatch ID: <strong>{selectedDispatch.dispatch_id}</strong></p>
                <p className="text-gray-600">ASC: <strong>{selectedDispatch.asc_name}</strong></p>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Repair Status *
                  </label>
                  <div className="flex gap-3">
                    <button
                      onClick={() => setReturnData({ ...returnData, repair_status: 'repaired' })}
                      className={`flex-1 py-3 rounded-lg border-2 font-semibold flex items-center justify-center gap-2 ${
                        returnData.repair_status === 'repaired'
                          ? 'bg-green-600 text-white border-green-600'
                          : 'bg-white text-gray-700 border-gray-300'
                      }`}
                    >
                      <CheckCircle className="w-5 h-5" />
                      Repaired
                    </button>
                    <button
                      onClick={() => setReturnData({ ...returnData, repair_status: 'not_repairable' })}
                      className={`flex-1 py-3 rounded-lg border-2 font-semibold flex items-center justify-center gap-2 ${
                        returnData.repair_status === 'not_repairable'
                          ? 'bg-red-600 text-white border-red-600'
                          : 'bg-white text-gray-700 border-gray-300'
                      }`}
                    >
                      <XCircle className="w-5 h-5" />
                      Not Repairable
                    </button>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Return Remarks
                  </label>
                  <textarea
                    value={returnData.remarks}
                    onChange={(e) => setReturnData({ ...returnData, remarks: e.target.value })}
                    rows="3"
                    placeholder="Any notes about the return..."
                    className="w-full px-4 py-2 border-2 rounded-lg"
                  />
                </div>

                <div className="p-3 bg-blue-50 rounded-lg border border-blue-200">
                  <p className="text-sm text-blue-900">
                    <strong>{returnData.returned_device_ids.length} device(s)</strong> will be marked as returned
                  </p>
                </div>
              </div>

              <div className="flex gap-3 mt-6">
                <button
                  onClick={() => {
                    setShowReturnModal(false);
                    setSelectedDispatch(null);
                  }}
                  className="flex-1 py-3 border-2 border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleReturn}
                  disabled={loading}
                  className="flex-1 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:bg-gray-300"
                >
                  {loading ? 'Processing...' : 'Receive Devices'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ASCDispatches;
